import { useRef, useState, useEffect } from 'react';
import { throttleToAnimationFrame, preloadImage } from '@utils/common';

/**
 * Hook that'll automatically preload the image of a model, ensuring that it is
 * fetched during the currently relevant dimensions.
 */
function useImage(args) {
  const largestWidthRef = useRef(0);
  const [state, setState] = useState(args.resolveImmediately ? {
    state: "success",
    src: args.src,
    width: args.width
  } : {
    state: "no-image-loaded"
  });
  const retryRef = useRef(() => {
    /* no-op */
  });
  useEffect(() => {
    if (args.resolveImmediately) {
      return;
    }
    const preload = async () => {
      // In case the container has not been fully rendered when trying to
      // preload the image initially, then try again in a little bit.
      // This WILL occur when the image is rendered within an Accordion, as an
      // example.
      if (args.containerRef.current?.clientWidth === 0) {
        setTimeout(preload, 50);
        return;
      }

      // Fallback to loading state in case we've previously failed before
      // loading an image.
      if (largestWidthRef.current === 0) {
        setState({
          state: "no-image-loaded"
        });
      }
      const nextWidthInterval = resolveImageWidth(args.containerRef, args.src, args.inLightbox);

      // In case we've already loaded an image bigger than the current image,
      // then simply bail out immediately.
      if (nextWidthInterval <= largestWidthRef.current) {
        return;
      }
      const cappedWidth = args.enableScaling ? nextWidthInterval : Math.min(args.width ?? Infinity, nextWidthInterval);
      const src = `${args.src}?w=${cappedWidth}`;
      try {
        const img = await preloadImage(src);

        // In case this image is bigger than any previously loaded images, then
        // set it as the new src to be used!
        if (img.naturalWidth > largestWidthRef.current) {
          setState({
            state: "success",
            src,
            width: img.naturalWidth,
            height: img.naturalHeight
          });
          largestWidthRef.current = img.naturalWidth;
        }
      } catch {
        // In case we have failed preloading our image (given that no previous
        // image has been loaded), then enter an error state. Else, we will
        // simply use the previously loaded image.
        if (largestWidthRef.current === 0) {
          setState({
            state: "error"
          });
        }
      }
    };
    preload();
    const throttled = throttleToAnimationFrame(preload);
    retryRef.current = preload;
    window.addEventListener("resize", throttled);
    window.addEventListener("orientationchange", throttled);
    return () => {
      window.removeEventListener("resize", throttled);
      window.removeEventListener("orientationchange", throttled);
    };
  }, [args.resolveImmediately, args.containerRef, args.enableScaling, args.width, args.src, args.inLightbox]);
  return {
    ...state,
    retry: retryRef.current
  };
}
const largestWidthCache = new Map();
function resolveImageWidth(containerRef, imgSrc, inLightbox) {
  const clientWidth = inLightbox ? window.innerWidth : containerRef.current?.clientWidth ?? window.innerWidth;
  const clientWidthInInterval = Math.ceil(clientWidth / 30) * 30;
  const width = clientWidthInInterval * window.devicePixelRatio;
  const previouslyLargestWidth = largestWidthCache.get(imgSrc);
  const nextWidth = Math.max(width, previouslyLargestWidth ?? 0);
  largestWidthCache.set(imgSrc, nextWidth);
  return nextWidth;
}

export { resolveImageWidth, useImage };
//# sourceMappingURL=useImage.js.map
